export type RefType = 'BRANCH' | 'TAG';

/**
 * Enum representing types of conflicts that can occur during component change detection.
 */
export enum ConflictType {
  FIELD_NAME_ALREADY_EXISTS = 'FIELD_NAME_ALREADY_EXISTS',
  TABLE_NAME_ALREADY_EXISTS = 'TABLE_NAME_ALREADY_EXISTS',
  FIELD_GROUP_NAME_ALREADY_EXISTS = 'FIELD_GROUP_NAME_ALREADY_EXISTS',
  PAGE_NAME_ALREADY_EXISTS = 'PAGE_NAME_ALREADY_EXISTS',
  DEFAULT_PAGE_ALREADY_EXISTS = 'DEFAULT_PAGE_ALREADY_EXISTS',
  TABLE_GROUP_NAME_ALREADY_EXISTS = 'TABLE_GROUP_NAME_ALREADY_EXISTS',
  INDEX_NAME_ALREADY_EXISTS = 'INDEX_NAME_ALREADY_EXISTS',
  FOREIGN_KEY_NAME_ALREADY_EXISTS = 'FOREIGN_KEY_NAME_ALREADY_EXISTS',
  LOOKUP_NAME_ALREADY_EXISTS = 'LOOKUP_NAME_ALREADY_EXISTS',
  WORKFLOW_NAME_ALREADY_EXISTS = 'WORKFLOW_NAME_ALREADY_EXISTS',
  WORKFLOW_ALREADY_EXISTS_FOR_TABLE = 'WORKFLOW_ALREADY_EXISTS_FOR_TABLE',
  CUSTOM_COMPONENT_NAME_ALREADY_EXISTS = 'CUSTOM_COMPONENT_NAME_ALREADY_EXISTS',
  TABLE_ACL_ACTION_NAME_ALREADY_EXISTS = 'TABLE_ACL_ACTION_NAME_ALREADY_EXISTS',
  SYNC_TIME_FIELD_MISSING = 'SYNC_TIME_FIELD_MISSING',
  TABLE_ACL_MISSING = 'TABLE_ACL_MISSING',
  DUPLICATE_TABLE_NAME = 'DUPLICATE_TABLE_NAME',
  PID_FIELD_MISSING = 'PID_FIELD_MISSING',
  SERVICE_MISSING = 'SERVICE_MISSING',
  QUERY_BUILDER_NAME_ALREADY_EXISTS = 'QUERY_BUILDER_NAME_ALREADY_EXISTS',
  APP_DOCUMENT_DESCRIPTION_ALREADY_EXISTS = 'APP_DOCUMENT_DESCRIPTION_ALREADY_EXISTS',
  ROLE_NAME_ALREADY_EXISTS = 'ROLE_NAME_ALREADY_EXISTS',
  ROLE_SHORT_NAME_ALREADY_EXISTS = 'ROLE_SHORT_NAME_ALREADY_EXISTS',
  DIAGRAM_NAME_ALREADY_EXISTS = 'DIAGRAM_NAME_ALREADY_EXISTS',
  PAGE_VARIABLE_NAME_ALREADY_EXISTS = 'PAGE_VARIABLE_NAME_ALREADY_EXISTS',
  PROVIDER_NAME_ALREADY_EXISTS = 'PROVIDER_NAME_ALREADY_EXISTS',
  APIGROUP_NAME_ALREADY_EXISTS = 'APIGROUP_NAME_ALREADY_EXISTS',
  CLIENT_NAME_ALREADY_EXISTS = 'CLIENT_NAME_ALREADY_EXISTS',
  FUNCTIONAL_COMPONENT_NAME_ALREADY_EXISTS = 'FUNCTIONAL_COMPONENT_NAME_ALREADY_EXISTS',
  TRANSLATION_ALREADY_EXISTS = 'TRANSLATION_ALREADY_EXISTS',
  IMPORT_NAME_ALREADY_EXISTS = 'IMPORT_NAME_ALREADY_EXISTS',
  WORKFLOW_ALREADY_EXISTS_FOR_TABLE_RUNTIME_CANNOT_BE_MERGED = 'WORKFLOW_ALREADY_EXISTS_FOR_TABLE_RUNTIME_CANNOT_BE_MERGED',
  RUNTIME_WORKFLOW_ALREADY_EXISTS_FOR_TABLE = 'RUNTIME_WORKFLOW_ALREADY_EXISTS_FOR_TABLE',
  QUERY_STRING_NAME_ALREADY_EXISTS = 'QUERY_STRING_NAME_ALREADY_EXISTS',
  APIINFO_NAME_ALREADY_EXISTS = 'APIINFO_NAME_ALREADY_EXISTS',
  SOFT_DELETE_OVERRIDE = 'SOFT_DELETE_OVERRIDE',
  SEARCH_PROVIDER_OVERRIDE = 'SEARCH_PROVIDER_OVERRIDE',
  EXPOSED_API_SERVICE_NAME_ALREADY_EXISTS = 'EXPOSED_API_SERVICE_NAME_ALREADY_EXISTS'
}

/**
 * Human-readable message keys for each conflict type (for i18n)
 */
export const CONFLICT_MESSAGE_KEYS: Record<ConflictType, string> = {
  [ConflictType.FIELD_NAME_ALREADY_EXISTS]: 'CONFLICT_FIELD_NAME_ALREADY_EXISTS',
  [ConflictType.TABLE_NAME_ALREADY_EXISTS]: 'CONFLICT_TABLE_NAME_ALREADY_EXISTS',
  [ConflictType.FIELD_GROUP_NAME_ALREADY_EXISTS]: 'CONFLICT_FIELD_GROUP_NAME_ALREADY_EXISTS',
  [ConflictType.PAGE_NAME_ALREADY_EXISTS]: 'CONFLICT_PAGE_NAME_ALREADY_EXISTS',
  [ConflictType.DEFAULT_PAGE_ALREADY_EXISTS]: 'CONFLICT_DEFAULT_PAGE_ALREADY_EXISTS',
  [ConflictType.TABLE_GROUP_NAME_ALREADY_EXISTS]: 'CONFLICT_TABLE_GROUP_NAME_ALREADY_EXISTS',
  [ConflictType.INDEX_NAME_ALREADY_EXISTS]: 'CONFLICT_INDEX_NAME_ALREADY_EXISTS',
  [ConflictType.FOREIGN_KEY_NAME_ALREADY_EXISTS]: 'CONFLICT_FOREIGN_KEY_NAME_ALREADY_EXISTS',
  [ConflictType.LOOKUP_NAME_ALREADY_EXISTS]: 'CONFLICT_LOOKUP_NAME_ALREADY_EXISTS',
  [ConflictType.WORKFLOW_NAME_ALREADY_EXISTS]: 'CONFLICT_WORKFLOW_NAME_ALREADY_EXISTS',
  [ConflictType.WORKFLOW_ALREADY_EXISTS_FOR_TABLE]: 'CONFLICT_WORKFLOW_ALREADY_EXISTS_FOR_TABLE',
  [ConflictType.CUSTOM_COMPONENT_NAME_ALREADY_EXISTS]: 'CONFLICT_CUSTOM_COMPONENT_NAME_ALREADY_EXISTS',
  [ConflictType.TABLE_ACL_ACTION_NAME_ALREADY_EXISTS]: 'CONFLICT_TABLE_ACL_ACTION_NAME_ALREADY_EXISTS',
  [ConflictType.SYNC_TIME_FIELD_MISSING]: 'CONFLICT_SYNC_TIME_FIELD_MISSING',
  [ConflictType.TABLE_ACL_MISSING]: 'CONFLICT_TABLE_ACL_MISSING',
  [ConflictType.DUPLICATE_TABLE_NAME]: 'CONFLICT_DUPLICATE_TABLE_NAME',
  [ConflictType.PID_FIELD_MISSING]: 'CONFLICT_PID_FIELD_MISSING',
  [ConflictType.SERVICE_MISSING]: 'CONFLICT_SERVICE_MISSING',
  [ConflictType.QUERY_BUILDER_NAME_ALREADY_EXISTS]: 'CONFLICT_QUERY_BUILDER_NAME_ALREADY_EXISTS',
  [ConflictType.APP_DOCUMENT_DESCRIPTION_ALREADY_EXISTS]: 'CONFLICT_APP_DOCUMENT_DESCRIPTION_ALREADY_EXISTS',
  [ConflictType.ROLE_NAME_ALREADY_EXISTS]: 'CONFLICT_ROLE_NAME_ALREADY_EXISTS',
  [ConflictType.ROLE_SHORT_NAME_ALREADY_EXISTS]: 'CONFLICT_ROLE_SHORT_NAME_ALREADY_EXISTS',
  [ConflictType.DIAGRAM_NAME_ALREADY_EXISTS]: 'CONFLICT_DIAGRAM_NAME_ALREADY_EXISTS',
  [ConflictType.PAGE_VARIABLE_NAME_ALREADY_EXISTS]: 'CONFLICT_PAGE_VARIABLE_NAME_ALREADY_EXISTS',
  [ConflictType.PROVIDER_NAME_ALREADY_EXISTS]: 'CONFLICT_PROVIDER_NAME_ALREADY_EXISTS',
  [ConflictType.APIGROUP_NAME_ALREADY_EXISTS]: 'CONFLICT_APIGROUP_NAME_ALREADY_EXISTS',
  [ConflictType.CLIENT_NAME_ALREADY_EXISTS]: 'CONFLICT_CLIENT_NAME_ALREADY_EXISTS',
  [ConflictType.FUNCTIONAL_COMPONENT_NAME_ALREADY_EXISTS]: 'CONFLICT_FUNCTIONAL_COMPONENT_NAME_ALREADY_EXISTS',
  [ConflictType.TRANSLATION_ALREADY_EXISTS]: 'CONFLICT_TRANSLATION_ALREADY_EXISTS',
  [ConflictType.IMPORT_NAME_ALREADY_EXISTS]: 'CONFLICT_IMPORT_NAME_ALREADY_EXISTS',
  [ConflictType.WORKFLOW_ALREADY_EXISTS_FOR_TABLE_RUNTIME_CANNOT_BE_MERGED]: 'CONFLICT_WORKFLOW_ALREADY_EXISTS_FOR_TABLE_RUNTIME_CANNOT_BE_MERGED',
  [ConflictType.RUNTIME_WORKFLOW_ALREADY_EXISTS_FOR_TABLE]: 'CONFLICT_RUNTIME_WORKFLOW_ALREADY_EXISTS_FOR_TABLE',
  [ConflictType.QUERY_STRING_NAME_ALREADY_EXISTS]: 'CONFLICT_QUERY_STRING_NAME_ALREADY_EXISTS',
  [ConflictType.APIINFO_NAME_ALREADY_EXISTS]: 'CONFLICT_APIINFO_NAME_ALREADY_EXISTS',
  [ConflictType.SOFT_DELETE_OVERRIDE]: 'CONFLICT_SOFT_DELETE_OVERRIDE',
  [ConflictType.SEARCH_PROVIDER_OVERRIDE]: 'CONFLICT_SEARCH_PROVIDER_OVERRIDE',
  [ConflictType.EXPOSED_API_SERVICE_NAME_ALREADY_EXISTS]: 'CONFLICT_EXPOSED_API_SERVICE_NAME_ALREADY_EXISTS'
};

/**
 * Enum for all valid merge statuses
 */
export enum MergeStatus {
  MERGE_INITIATED = 'MERGE_INITIATED',
  MERGE_FAILED = 'MERGE_FAILED',
  MERGE_COMPLETED = 'MERGE_COMPLETED',
  MERGE_VALIDATION_IN_PROGRESS = 'MERGE_VALIDATION_IN_PROGRESS',
  MERGE_TAG_CREATION_STARTED = 'MERGE_TAG_CREATION_STARTED',
  MERGE_PROCESS_IN_PROGRESS = 'MERGE_PROCESS_IN_PROGRESS',
  MERGE_CHANGES_REVIEW_IN_PROGRESS = 'MERGE_CHANGES_REVIEW_IN_PROGRESS',
  PUBLISH_PROCESS_STARTED = 'PUBLISH_PROCESS_STARTED',
  MERGE_COMPLETED_WITH_PUBLISH_ERROR = 'MERGE_COMPLETED_WITH_PUBLISH_ERROR',
  MERGE_CANCELLED = 'MERGE_CANCELLED'
}

/**
 * Statuses that indicate actual merge is running (not just review)
 */
export const ACTIVE_MERGE_STATUSES: MergeStatus[] = [
  MergeStatus.MERGE_VALIDATION_IN_PROGRESS,
  MergeStatus.MERGE_TAG_CREATION_STARTED,
  MergeStatus.MERGE_PROCESS_IN_PROGRESS,
  MergeStatus.PUBLISH_PROCESS_STARTED
];

/**
 * Statuses to listen for in Firestore (active merge + review in progress)
 */
export const MERGE_LISTENER_STATUSES: string[] = [
  MergeStatus.MERGE_CHANGES_REVIEW_IN_PROGRESS,
  MergeStatus.MERGE_VALIDATION_IN_PROGRESS,
  MergeStatus.MERGE_TAG_CREATION_STARTED,
  MergeStatus.MERGE_PROCESS_IN_PROGRESS,
  MergeStatus.PUBLISH_PROCESS_STARTED,
  MergeStatus.MERGE_CANCELLED,
  MergeStatus.MERGE_COMPLETED,
  MergeStatus.MERGE_COMPLETED_WITH_PUBLISH_ERROR,
  MergeStatus.MERGE_FAILED
];

export interface MenuItem {
  label?: string;
  icon?: string;
  command?: () => void;
  disabled?: boolean;
  tooltipOptions?: { tooltipLabel?: string; tooltipPosition?: string };
  separator?: boolean;
}

export interface FilterOption {
  label: string;
  value: string;
}

export interface BranchData {
  sid?: string;
  refsName: string;
  defaultAppSid: string;
  parentAppSid?: string;
  isDefaultBranch?: boolean;
  gitRefsName?: string;
  sourceBranchAppSid?: string;
  sourceBranchName?: string;
  createdBy?: string;
  createdByName?: string;
  viaMerge?: boolean;
  createdByAvatar?: string;
  createdDate?: number;
  createdOn?: number;
  branchCreatedDate?: number;
  modifiedBy?: string;
  modifiedDate?: number;
  lastUpdatedBy?: string;
  lastUpdatedByName?: string;
   lastUpdatedByAvatar?: string;
  shortName?: string;
  comments?: string;
  lastMergedBy?: string;
  lastMergedByName?: string;
  lastMergedByAvatar?: string;
  lastMergedDate?: string;
  rappitVersion?: string;
  name?: string;
  orgId?: string;
  ownerEmail?: string;
  ownerName?: string;
  packageName?: string;
  refType?: string;
  repositoryName?: string;
  projectUrl?: string;
  serviceName?: string;
  sourceAppId?: string;
  sourceOrgId?: string;
  status?: string;
  applicationType?: 'INDEPENDENT' | 'MEMBER' | 'SUITE' | '';
  suiteId?: string;
  prototypingAppUrl?: string;
  projectCreated?: boolean;
  isPrototypePublished?: boolean;
  defaultApp?: boolean;
  recDeleted?: boolean;
}

export interface TagData {
  sid?: string;
  refsName: string;
  gitRefsName?: string;
  isDefaultBranch?: boolean;
  createdFrom?: string;
  createdFromName?: string;
  createdBy?: string;
  createdByName?: string;
  viaMerge?: boolean;
  createdByAvatar?: string;
  createdDate?: string;
  createdOn?: number;
  lastUpdated?: string;
  lastUpdatedBy?: string;
  lastUpdatedByName?: string;
  lastUpdatedByAvatar?: string;
  sourceBranchAppSid?: string;
  sourceBranchName?: string;
  comments?: string;
  tagId?: string;
  refType?: string;
  projectUrl?: string;
  applicationType?: 'INDEPENDENT' | 'MEMBER' | 'SUITE' | '';
  name?: string;
  parentAppSid?: string;
  defaultAppSid?: string;
  suiteId?: string;
}

/** Groups all per-app tag rows under a single suite tag (refsName). */
export interface SuiteTagCard {
  refsName: string;
  /** Total number of apps (suite + members) that have this tag. */
  appCount: number;
  /** Suite app's own tag data — used for the group header summary. */
  suiteTagData: TagData;
  suiteAppName: string;
  /** All per-app rows (suite first, then members). */
  childRows: any[];
  expanded: boolean;
}

export interface BranchTreeNode {
  data: BranchData;
}

export interface TagTreeNode {
  data: TagData;
}

export interface SuiteAppGroup {
  appName: string;
  appSid: string;
  isSuiteParent: boolean;
  branches: BranchData[];
  expanded: boolean;
  loading: boolean;
  branchCount: number;
}

export interface SuiteTagGroup {
  appName: string;
  appSid: string;
  isSuiteParent: boolean;
  tags: TagData[];
  expanded: boolean;
  loading: boolean;
  tagCount: number;
}

/** Groups all per-app branch rows under a single suite branch (refsName). */
export interface SuiteBranchGroup {
  refsName: string;
  /** Total number of apps (suite + members) that have this branch. */
  appCount: number;
  /** Suite app's own branch data — used for the group header summary. */
  suiteBranchData: BranchData;
  suiteAppName: string;
  /** All per-app rows (suite first, then members). */
  childRows: any[];
  expanded: boolean;
}

export interface PaginationState<T> {
  page: number;
  pageSize: number;
  hasMore: boolean;
  data: T[];
}

export interface BranchFilters {
  refsName?: string;
  gitRefsName?: string;
  sourceBranchAppSid?: string;
  sourceBranchName?: string;
  createdBy?: string;
  lastUpdatedBy?: string;
  shortName?: string;
  lastMergedBy?: string;
  lastMergedDate?: string;
}

export interface TagFilters {
  refsName?: string;
  createdFrom?: string;
  createdBy?: string;
  createdDate?: string;
  lastUpdated?: string;
  sourceBranchAppSid?: string;
  sourceBranchName?: string;
}

export interface LastUpdatedChange {
  sid?: string;
  componentId: string;
  componentType: string;
  componentName?: string;
  action: string;
  modifiedBy: string;
  modifiedDate: number;
}

export interface LastUpdatedDetails {
  lastUpdatedOn?: string;
  lastUpdatedBy?: string;
  modifiedBy?: string;
  modifiedByName?: string;
  modifiedDate?: number;
  updatedUserName?: string;
  changes: LastUpdatedChange[];
}

export const DEFAULT_BRANCH_FILTERS: BranchFilters = {};
export const DEFAULT_TAG_FILTERS: TagFilters = {};
export const DEFAULT_PAGE_SIZE = 50;

// Merge Changes Types
export type MergeAction = 'MERGE_SOURCE' | 'MERGE_TARGET' | 'MERGE_LATER';
export type RecordChangeType = 'ADDED' | 'MODIFIED' | 'RENAMED' | 'DELETED';

export interface ComponentAdditionalInfo {
  sourceName?: string;
  targetName?: string;
  sourcePath?: string;  // Hierarchical path for source (e.g., "Table: QVTABLE > Fields For Unique Record For Lookup")
  targetPath?: string;  // Hierarchical path for target
  workflowId?: string;
  tableId?: string;  // Used for Query View grouping
  queryViewName?: string; // Name of the query view
  [key: string]: unknown;
}

export interface ComponentDetail {
  componentId: string;
  componentType: string;
  recordChangeType: RecordChangeType;
  dependentComponents: Record<string, unknown>;
  lastSourceModification: string | null;
  lastSourceModifier: string | null;
  lastTargetModification: string | null;
  lastTargetModifier: string | null;
  additionalRecordChangeTypeList: string[];
  modelActionTypes: Record<string, unknown>;
  additionalInfo: ComponentAdditionalInfo;
  conflicts: ConflictType[];
  applicationName?: string;
  sourceAppId?: string;
  targetAppId?: string;
}

export interface MergeChangesResponse {
  // Nested structure: Map<ReferenceModelType, Map<String, ComponentDetail>>
  componentChangeDetails: Record<string, Record<string, ComponentDetail>>;
  mergeId: string;
  suiteMerge?: boolean;
  memberAppCount?: number;
}

export interface MergeChangesRequest {
  sourceBranchId: string;
  targetBranchId: string;
  ignoreOnGoingMergeValidation?: boolean;
}

export interface MergeChange {
  id: string;
  componentId: string;
  componentType: string;
  recordChangeType: RecordChangeType;
  additionalRecordChangeTypeList?: string[];
  sourceComponentName: string;
  targetComponentName: string;
  sourceComponentPath?: string;  // Hierarchical path for source component
  targetComponentPath?: string;  // Hierarchical path for target component
  lastSourceModification: string | null;
  lastSourceModifier: string | null;
  lastTargetModification: string | null;
  lastTargetModifier: string | null;
  hasConflict: boolean;
  conflicts: ConflictType[];
  action: MergeAction;
  hasError: boolean;
  isAutoSelected?: boolean;
  // Workflow grouping fields
  workflowId?: string;
  groupedComponentIds?: string[];  // All component IDs in this workflow group
  groupedComponentTypes?: Record<string, string>;  // Map of componentId -> componentType for grouped components
  isGroupRepresentative?: boolean; // True if this row represents a group (for display)
  isSyntheticWorkflow?: boolean; // True if this is a synthetic workflow row (not a real component, should not be sent to API)
  workflowName?: string; // Name of the workflow (used for synthetic workflow display)
  // Query View grouping fields
  tableId?: string; // Links child components (QUERY_VIEW_FIELD, etc.) to parent QUERY_VIEW
  isSyntheticQueryView?: boolean; // True if this is a synthetic query view row
  queryViewName?: string; // Name of the query view (used for synthetic query view display)
  isSyntheticEnvironment?: boolean; // True if this is a synthetic environment group row
  environmentName?: string; // Name of the environment group (used for synthetic environment display)
  isSyntheticTechChoices?: boolean; // True if this is a synthetic technology choices group row
  applicationName?: string; // e.g., "OrderService", "UserService"
  sourceAppId?: string; // Source branch SID for this component's app
  targetAppId?: string; // Target branch SID for this component's app
  groupedComponentAppIds?: Record<string, { sourceAppId: string; targetAppId: string }>; // Per-component app IDs for env/techChoices groups
  additionalInfo?: Record<string, string>; // Additional info for display (e.g., source/target paths, workflowId, etc.)
}

// Step 3: Auto-select API types
export interface AutoSelectRequest {
  selectedComponents: Record<string, string[]>;
  sourceBranchId: string;
  targetBranchId: string;
  ignoreOnGoingMergeValidation: boolean;
  mergeId?: string;
}

export interface ReviewMergeChange extends MergeChange {
  isAutoSelected: boolean;
  /** Component IDs that this auto-selected component depends on (why it was included) */
  dependentComponentIds?: string[];
  /** Names of components that this auto-selected component depends on (for display) */
  dependentComponentNames?: string[];
  /** Model action types map: key is modelId, value is actionType - used for references lookup */
  modelActionTypes?: Record<string, string>;
}

/** Grouped structure for Step 3 review display - manual component with its auto-selected dependencies */
export interface GroupedReviewChange {
  /** The manual (user-selected) component */
  parent: ReviewMergeChange;
  /** Auto-selected components that depend on this parent */
  children: ReviewMergeChange[];
  /** Whether this group is expanded in the UI */
  expanded: boolean;
}

// Step 3: Merge API types
export interface MergeBranchRequest {
  selectedComponentIds: string[];
  branchMergeDataList: MergeBranchComponentSet[];
  componentsMap?: Record<string, string[]>;
  componentActionsMap?: Record<string, string>; // componentId -> action (ADDED, MODIFIED, RENAMED, DELETED)
  unselectedComponents?: Record<string, string[]>;
  targetselectedComponents?: Record<string, string[]>;
  tagName: string;
  mergeId: string;
  canPublish: boolean;
  memberAppDetails?: Record<string, string[]>; // Map of added or deleted apps
}

/** Per-branch component set for suite merges */
export interface MergeBranchComponentSet {
  sourceBranchId: string;
  targetBranchId: string;
  componentsMap: Record<string, string[]>;
  unselectedComponents: Record<string, string[]>;
  targetselectedComponents: Record<string, string[]>;
  componentActionsMap: Record<string, string>;
}

// Merge eligibility check response
export interface MergeEligibilityResponse {
  eligible: boolean;
  reason?: string;
}
